import React from 'react';
import { Container } from 'react-bootstrap';
import { ScrollText, Reveal } from '../../components/motion';
import { FaBullseye, FaEye } from 'react-icons/fa';
import Icon from '../../components/ui/Icon';
import heroImg from '../../assets/images/about-hero.webp';
import usePageMeta from '../../hooks/usePageMeta';
import Eyebrow from '../../components/ui/Eyebrow';
import logoUrl from '../../assets/images/logo.svg';
import { COUNTRIES } from '../Services/content/shared';
import styles from './About.module.css';

/** Flag chips in the same order the first paragraph names the countries. */
const FLAG_ORDER = [
  'India',
  'Pakistan',
  'Philippines',
  'Bangladesh',
  'Nepal',
  'Sri Lanka',
  'Malaysia',
  'Singapore',
  'Brunei',
  'United Kingdom',
];
const FLAGS = FLAG_ORDER.map((n) => COUNTRIES.find((c) => c.name === n)).filter(
  (c): c is { code: string; name: string } => !!c,
);

const STATS = [
  { value: '12', label: 'Branches in KSA' },
  { value: '10', label: 'Countries' },
  { value: '1', label: 'Tracking number per shipment' },
];

const About: React.FC = () => {
  usePageMeta({
    title: 'About Sahari Cargo — Cargo Company in Riyadh, Saudi Arabia',
    description:
      'Sahari Cargo is a Riyadh-based cargo and courier company with 12 branches across Saudi Arabia, shipping by air and sea to 10 countries including India, Pakistan, the Philippines and the UK.',
    url: 'https://www.saharicargo.com/about',
  });

  return (
    <div className={styles.page}>
      <Container fluid="xl" className={styles.inner}>
        {/* Split hero: title + first paragraph + flags | photo */}
        <div className={styles.hero}>
          <div className={styles.heroText}>
            <Eyebrow>About us</Eyebrow>
            <ScrollText as="h1" mode="enter" className={styles.title}>
              About Sahari Cargo
            </ScrollText>
            <Reveal as="p" className={styles.subtitle} delay={0.1}>
              Cargo &amp; Courier Company in Riyadh, Saudi Arabia
            </Reveal>
            <Reveal as="p" className={styles.para} delay={0.15}>
              Sahari Cargo is a cargo and courier company headquartered in Riyadh, Saudi Arabia, with 12 branches across
              the Kingdom — from Duba in the north to Jizan in the south, including Jeddah, Makkah, Madinah, Taif,
              Yanbu, Abha, and Khamis Mushait. We help families and businesses send cargo from Saudi Arabia to India,
              Pakistan, the Philippines, Bangladesh, Nepal, Sri Lanka, Malaysia, Singapore, Brunei, and the United
              Kingdom.
            </Reveal>
            <Reveal delay={0.2}>
              <ul className={styles.flags}>
                {FLAGS.map((c) => (
                  <li key={c.code}>
                    <img
                      src={`https://flagcdn.com/w40/${c.code}.png`}
                      srcSet={`https://flagcdn.com/w80/${c.code}.png 2x`}
                      alt=""
                      width={20}
                      height={14}
                      loading="lazy"
                    />
                    {c.name}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal className={styles.photo} delay={0.05}>
            <img src={heroImg} alt="Sahari Cargo courier delivering a package" className={styles.heroImg} />
            <span className={styles.badge}>
              <img src={logoUrl} alt="Sahari Cargo" />
            </span>
          </Reveal>
        </div>

        <ul className={styles.stats}>
          {STATS.map((s) => (
            <li key={s.label}>
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </li>
          ))}
        </ul>

        {/* Story */}
        <div className={styles.story}>
          <div className={styles.storyHead}>
            <Eyebrow>How we work</Eyebrow>
            <ScrollText as="h2" className={styles.h2}>
              One company, from pickup to delivery
            </ScrollText>
          </div>
          <div>
            <Reveal as="p" className={styles.para}>
              Our services cover the whole journey: courier within Saudi Arabia and abroad, airport to airport air
              cargo, door-to-door air cargo and sea freight, professional packing, warehousing and storage, and cargo
              insurance. Export documents in Saudi Arabia and import customs at the destination are handled by our team,
              so the recipient simply receives their shipment.
            </Reveal>
            <Reveal as="p" className={styles.para}>
              Every shipment runs through our own system. Each box is packed, photographed, and given its own number,
              then tracked from pickup to delivery — so when you ask where your cargo is, we can tell you exactly, box
              by box.
            </Reveal>
            <Reveal as="p" className={styles.para}>
              Whether it is a single parcel for family back home or regular commercial shipments, you deal with one
              company from start to finish: one booking, one tracking number, and a branch near you to call or walk into
              when you need an answer.
            </Reveal>
          </div>
        </div>

        {/* Mission + vision */}
        <div className={styles.mvGrid}>
          <Reveal className={`${styles.mvCard} ${styles.mvRed}`}>
            <span className={styles.mvIcon} aria-hidden="true">
              <Icon icon={FaBullseye} />
            </span>
            <h2 className={styles.mvTitle}>Our Mission</h2>
            <p className={styles.mvText}>
              To make sending cargo from Saudi Arabia simple, safe, and transparent — with honest pricing, customs
              handled for you, and tracking you can rely on for every box.
            </p>
          </Reveal>
          <Reveal className={`${styles.mvCard} ${styles.mvDark}`} delay={0.08}>
            <span className={styles.mvIcon} aria-hidden="true">
              <Icon icon={FaEye} />
            </span>
            <h2 className={styles.mvTitle}>Our Vision</h2>
            <p className={styles.mvText}>
              To be the most trusted cargo company for families and businesses shipping from Saudi Arabia, and to keep
              adding the routes and services our customers ask for.
            </p>
          </Reveal>
        </div>
      </Container>
    </div>
  );
};

export default About;
